import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWervenByDate, searchWerven } from '@/api/warehouse';
import type { WerfCard } from '@/api/warehouse';
import { WarehouseWerfCard } from '@/components/warehouse/WarehouseWerfCard';
import {
  todayLocalStr,
  shiftDateStr,
  formatDayLabel,
  isToday,
} from '@/utils/warehouse';

const SEARCH_DEBOUNCE_MS = 300;

export function WarehouseWervenView() {
  const navigate = useNavigate();

  const [date, setDate] = useState<string>(todayLocalStr());
  const [werven, setWerven] = useState<WerfCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const isSearching = searchTerm.trim().length > 0;

  // Debounce the search input → searchTerm.
  useEffect(() => {
    const handle = setTimeout(() => setSearchTerm(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // A request token prevents a slow in-flight response from overwriting newer state.
  const reqTokenRef = useRef(0);

  const load = useCallback(async () => {
    const token = ++reqTokenRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = isSearching
        ? await searchWerven(searchTerm.trim())
        : await fetchWervenByDate(date);
      if (token !== reqTokenRef.current) return; // stale
      setWerven(res.werven);
    } catch (err) {
      if (token !== reqTokenRef.current) return;
      setError(err instanceof Error ? err.message : 'Werven ophalen mislukt');
      setWerven([]);
    } finally {
      if (token === reqTokenRef.current) setLoading(false);
    }
  }, [date, searchTerm, isSearching]);

  useEffect(() => {
    load();
  }, [load]);

  const openWerf = useCallback(
    (werf: WerfCard) => {
      const params = new URLSearchParams();
      if (werf.faseTaskId) params.set('fase', String(werf.faseTaskId));
      const qs = params.toString();
      navigate(`/werven/${werf.projectId}${qs ? `?${qs}` : ''}`, {
        state: {
          projectName: werf.projectName,
          werfAddress: werf.werfAddress,
          saleOrderName: werf.saleOrderName,
        },
      });
    },
    [navigate],
  );

  const plannedCount = werven.length;

  return (
    <div className="mx-auto max-w-lg lg:max-w-3xl">
      {/* Light header — fits the portal palette (white cards on a light page) */}
      <header className="px-3 pt-4 pb-1">
        <div className="px-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-td-muted leading-none">Magazijn</p>
          <h1 className="text-xl font-extrabold text-td-navy leading-tight">Werven</h1>
        </div>

        {/* Day switcher */}
        <div className="mt-3 flex items-center justify-between rounded-xl bg-white border border-gray-200 shadow-sm px-2 py-1.5">
          <button
            type="button"
            onClick={() => setDate((d) => shiftDateStr(d, -1))}
            disabled={isSearching}
            className="p-1.5 rounded-lg text-td-muted hover:bg-gray-100 disabled:opacity-30"
            aria-label="Vorige dag"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24" aria-hidden="true">
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <div className="text-center">
            <p className="text-sm font-bold text-td-navy leading-none">
              {isToday(date) ? 'Vandaag · ' : ''}
              {formatDayLabel(date)}
            </p>
            <p className="text-[10px] text-td-muted mt-0.5">
              {isSearching
                ? `${plannedCount} ${plannedCount === 1 ? 'resultaat' : 'resultaten'}`
                : `${plannedCount} ${plannedCount === 1 ? 'werf' : 'werven'} gepland`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setDate((d) => shiftDateStr(d, 1))}
            disabled={isSearching}
            className="p-1.5 rounded-lg text-td-muted hover:bg-gray-100 disabled:opacity-30"
            aria-label="Volgende dag"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24" aria-hidden="true">
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
        </div>

        {/* Search over all days */}
        <div className="mt-2 flex items-center gap-2 rounded-xl bg-white border border-gray-200 shadow-sm px-3 py-2 focus-within:border-td-blue/50">
          <svg className="w-4 h-4 text-td-light shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Zoek werf, klant of S-nummer…"
            aria-label="Zoek werf, klant of S-nummer"
            className="flex-1 bg-transparent text-sm text-td-navy placeholder:text-td-light focus:outline-none"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput('')}
              className="p-0.5 rounded text-td-light hover:text-td-muted"
              aria-label="Zoekopdracht wissen"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <p className="mt-1 px-1 text-[10px] text-td-light">
          Zoekt over <b>alle dagen</b>, niet enkel de gekozen dag.
        </p>
      </header>

      <div className="px-3 py-3 space-y-2.5">
        {loading && (
          <div className="py-12 text-center" role="status">
            <div className="inline-block w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin">
              <span className="sr-only">Laden...</span>
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-xl" role="alert">
            <p className="font-semibold">Fout bij laden</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        )}

        {!loading && !error && werven.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-td-muted text-sm">
              {isSearching ? 'Geen werven gevonden voor deze zoekopdracht.' : 'Geen werven gepland op deze dag.'}
            </p>
          </div>
        )}

        {!loading && !error &&
          werven.map((werf) => (
            <WarehouseWerfCard key={werf.faseTaskId} werf={werf} onOpen={openWerf} searchMode={isSearching} />
          ))}
      </div>
    </div>
  );
}
